import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  MessageSquare, 
  Calendar, 
  Palette, 
  Code, 
  ChevronRight, 
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Check,
  CheckCircle,
  Circle,
  Loader2,
  Upload,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Save,
  Sparkles,
  Rocket,
  Database,
  FileSpreadsheet,
  Star,
  Phone,
  Mail,
  BookOpen,
  Play,
  HelpCircle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: 'Branding', description: 'Customize look', icon: Palette },
  { id: 2, title: 'CMS', description: 'Export format', icon: Database },
  { id: 3, title: 'Embed', description: 'Install widget', icon: Code },
  { id: 4, title: 'Done!', description: 'Get started', icon: Rocket },
];

const SetupWizardPage = () => {
  const navigate = useNavigate();
  const { user, completeSetup } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Calendar state
  const [integrations, setIntegrations] = useState(null);
  const [calendlyToken, setCalendlyToken] = useState('');
  const [showCalendlyToken, setShowCalendlyToken] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState(null);

  // Branding state
  const [branding, setBranding] = useState({
    logo_url: '',
    primary_color: '#0d9488',
    widget_greeting: '',
    widget_title: '',
    firm_phone: '',
    firm_email: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // CMS preference state
  const [preferredCms, setPreferredCms] = useState('universal');
  const [showAllCms, setShowAllCms] = useState(false);
  
  // Popular CMS options shown prominently
  const POPULAR_CMS = [
    { id: 'filevine', name: 'Filevine', popular: true },
    { id: 'clio', name: 'Clio', popular: true },
    { id: 'mycase', name: 'MyCase', popular: true },
    { id: 'casepeer', name: 'CASEpeer', popular: true },
    { id: 'prevail', name: 'Prevail', popular: true },
  ];
  
  const CMS_OPTIONS = [
    { id: 'universal', name: 'Universal (All Fields)', category: 'default' },
    // Legal CMS
    { id: 'filevine', name: 'Filevine', category: 'legal' },
    { id: 'clio', name: 'Clio', category: 'legal' },
    { id: 'mycase', name: 'MyCase', category: 'legal' },
    { id: 'practicepanther', name: 'PracticePanther', category: 'legal' },
    { id: 'smokeball', name: 'Smokeball', category: 'legal' },
    { id: 'casepeer', name: 'CASEpeer', category: 'legal' },
    { id: 'litify', name: 'Litify', category: 'legal' },
    { id: 'needles', name: 'Needles', category: 'legal' },
    { id: 'smartadvocate', name: 'Smart Advocate', category: 'legal' },
    { id: 'cloudlex', name: 'CloudLex', category: 'legal' },
    { id: 'lawmatics', name: 'Lawmatics', category: 'legal' },
    { id: 'rocketmatter', name: 'Rocket Matter', category: 'legal' },
    { id: 'cosmolex', name: 'CosmoLex', category: 'legal' },
    { id: 'abacuslaw', name: 'AbacusLaw', category: 'legal' },
    { id: 'pclaw', name: 'PCLaw', category: 'legal' },
    { id: 'tabs3', name: 'Tabs3', category: 'legal' },
    { id: 'timesolv', name: 'TimeSolv', category: 'legal' },
    { id: 'leaplegal', name: 'LEAP Legal', category: 'legal' },
    // Plaintiff
    { id: 'trialworks', name: 'TrialWorks', category: 'plaintiff' },
    { id: 'prevail', name: 'Prevail', category: 'plaintiff' },
    { id: 'simplyconvert', name: 'SimplyConvert', category: 'plaintiff' },
    // CRM
    { id: 'salesforce', name: 'Salesforce', category: 'crm' },
    { id: 'hubspot', name: 'HubSpot', category: 'crm' },
    { id: 'zohocrm', name: 'Zoho CRM', category: 'crm' },
    { id: 'monday', name: 'Monday.com', category: 'crm' },
  ];

  // Embed state
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [integrationsRes, brandingRes, cmsRes] = await Promise.all([
        axios.get(`${API}/integrations/status`),
        axios.get(`${API}/branding`),
        axios.get(`${API}/cms-preference`).catch(() => ({ data: { preferred_cms_format: 'universal' } }))
      ]);
      setIntegrations(integrationsRes.data);
      setBranding(brandingRes.data);
      setPreferredCms(cmsRes.data?.preferred_cms_format || 'universal');
      
      // Check if any calendar is already connected
      if (integrationsRes.data?.calendly?.valid) {
        setSelectedCalendar('calendly');
      } else if (integrationsRes.data?.google_calendar?.configured) {
        setSelectedCalendar('google');
      } else if (integrationsRes.data?.outlook?.configured) {
        setSelectedCalendar('outlook');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (user) {
      const script = `<!-- IntakePal Chat Widget -->
<script>
  window.intakepalConfig = {
    firmId: "${user.id}",
    primaryColor: "${branding.primary_color}"
  };
</script>
<script src="${process.env.REACT_APP_BACKEND_URL}/widget.js" async></script>`;
      setEmbedCode(script);
    }
  }, [user, branding.primary_color]);

  const saveCalendly = async () => {
    if (!calendlyToken.trim()) {
      toast.error('Please enter your Calendly API token');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/auth/integrations`, 
        { calendly_token: calendlyToken },
        
      );
      toast.success('Calendly connected!');
      setCalendlyToken('');
      setSelectedCalendar('calendly');
      fetchData();
    } catch (error) {
      toast.error('Failed to connect Calendly');
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/branding`, branding);
      toast.success('Branding saved!');
    } catch (error) {
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/branding/logo`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBranding(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const copyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success('Embed code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCompleteSetup = async () => {
    try {
      await completeSetup(); // Call the AuthContext function
    } catch (error) {
      console.error('Failed to mark setup as complete:', error);
    }
    toast.success('Setup complete! Let\'s activate your account.');
    // Redirect to subscription page for new users
    navigate('/subscription');
  };

  const saveCmsPreference = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms-preference?cms_format=${preferredCms}`);
      toast.success('CMS preference saved!');
    } catch (error) {
      toast.error('Failed to save CMS preference');
    } finally {
      setSaving(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipStep = () => {
    if (currentStep < 4) {
      nextStep();
    } else {
      handleCompleteSetup();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleCompleteSetup}
              className="text-slate-500"
              data-testid="skip-setup-btn"
            >
              Skip Setup
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Quick Setup
          </div>
          <h1 
            className="text-3xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Welcome, {user?.full_name || user?.firm_name}!
          </h1>
          <p className="text-slate-600">
            Let&apos;s get your intake system ready in just a few minutes.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div 
                className={`flex flex-col items-center cursor-pointer transition-all ${
                  currentStep === step.id ? 'scale-105' : ''
                }`}
                onClick={() => setCurrentStep(step.id)}
              >
                <div 
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    currentStep > step.id 
                      ? 'bg-teal-500 text-white' 
                      : currentStep === step.id 
                        ? 'bg-teal-500 text-white ring-4 ring-teal-200' 
                        : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <step.icon className="w-5 h-5" />
                  )}
                </div>
                <span className={`text-sm mt-2 font-medium ${
                  currentStep >= step.id ? 'text-teal-600' : 'text-slate-400'
                }`}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div 
                  className={`w-16 h-1 mx-2 rounded transition-all ${
                    currentStep > step.id ? 'bg-teal-500' : 'bg-slate-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="border-slate-200 shadow-lg">

          {/* Step 1: Branding */}
          {currentStep === 1 && (
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Customize Your Brand
                </CardTitle>
                <CardDescription className="text-base">
                  Make the chat widget match your firm&apos;s identity
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-4">
                {/* Logo Upload */}
                <div className="space-y-3">
                  <Label>Firm Logo</Label>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center cursor-pointer hover:border-teal-400 transition-colors overflow-hidden bg-slate-50"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {branding.logo_url ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`} 
                          alt="Logo" 
                          className="w-full h-full object-contain"
                        />
                      ) : uploadingLogo ? (
                        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                      ) : (
                        <Upload className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingLogo}
                        data-testid="wizard-upload-logo"
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-slate-500 mt-1">PNG, JPG up to 2MB</p>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>

                {/* Primary Color */}
                <div className="space-y-3">
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-4">
                    <input
                      type="color"
                      value={branding.primary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-12 h-12 rounded-lg cursor-pointer border-0"
                      data-testid="wizard-primary-color"
                    />
                    <Input
                      value={branding.primary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-32 font-mono"
                      data-testid="wizard-primary-color-input"
                    />
                    <div className="flex gap-2">
                      {['#0d9488', '#3b82f6', '#8b5cf6', '#ef4444', '#f59e0b'].map(color => (
                        <button
                          key={color}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            branding.primary_color === color ? 'border-slate-900 scale-110' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => setBranding(prev => ({ ...prev, primary_color: color }))}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Widget Title */}
                <div className="space-y-2">
                  <Label>Widget Title</Label>
                  <Input
                    placeholder="e.g., Smith & Associates"
                    value={branding.widget_title}
                    onChange={(e) => setBranding(prev => ({ ...prev, widget_title: e.target.value }))}
                    data-testid="wizard-widget-title"
                  />
                </div>

                {/* Widget Greeting */}
                <div className="space-y-2">
                  <Label>Welcome Message</Label>
                  <Textarea
                    placeholder="e.g., Hello! Have you been injured in an accident? I'm here to help you get started."
                    value={branding.widget_greeting}
                    onChange={(e) => setBranding(prev => ({ ...prev, widget_greeting: e.target.value }))}
                    rows={3}
                    data-testid="wizard-widget-greeting"
                  />
                </div>

                {/* Firm Contact Info */}
                <div className="space-y-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Phone className="w-4 h-4" />
                    <span className="font-semibold text-sm">Firm Contact Info for Client Emails</span>
                  </div>
                  <p className="text-xs text-amber-700">
                    This information will appear in follow-up emails sent to potential clients.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Firm Phone</Label>
                      <Input
                        placeholder="(555) 123-4567"
                        value={branding.firm_phone || ''}
                        onChange={(e) => setBranding(prev => ({ ...prev, firm_phone: e.target.value }))}
                        className="mt-1"
                        data-testid="wizard-firm-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Firm Email</Label>
                      <Input
                        type="email"
                        placeholder="contact@yourfirm.com"
                        value={branding.firm_email || ''}
                        onChange={(e) => setBranding(prev => ({ ...prev, firm_email: e.target.value }))}
                        className="mt-1"
                        data-testid="wizard-firm-email"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="p-4 bg-slate-100 rounded-xl">
                  <p className="text-sm font-medium text-slate-600 mb-3">Preview</p>
                  <div className="bg-white rounded-xl shadow-lg p-4 max-w-xs">
                    <div className="flex items-center gap-2 mb-3">
                      {branding.logo_url ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`} 
                          alt="Logo" 
                          className="w-8 h-8 rounded object-contain"
                        />
                      ) : (
                        <div 
                          className="w-8 h-8 rounded flex items-center justify-center"
                          style={{ backgroundColor: branding.primary_color }}
                        >
                          <MessageSquare className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <span className="font-semibold text-sm">
                        {branding.widget_title || user?.firm_name || 'Your Firm'}
                      </span>
                    </div>
                    <div 
                      className="p-3 rounded-lg text-white text-sm"
                      style={{ backgroundColor: branding.primary_color }}
                    >
                      {branding.widget_greeting || "Hello! Have you been injured in an accident? I'm here to help."}
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={saveBranding} 
                  disabled={saving}
                  className="w-full bg-teal-500 hover:bg-teal-600"
                  data-testid="wizard-save-branding"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Branding
                </Button>
              </CardContent>
            </>
          )}

          {/* Step 2: CMS Preference */}
          {currentStep === 2 && (
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Choose Your CMS
                </CardTitle>
                <CardDescription className="text-base">
                  Select your case management system for formatted lead exports
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-4">
                {/* Info Box */}
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Automatic CSV Attachments</h4>
                      <p className="text-sm text-blue-800">
                        When you receive a new lead notification email, we&apos;ll automatically attach a 
                        CSV file formatted for your CMS. Simply import it directly!
                      </p>
                    </div>
                  </div>
                </div>

                {/* CMS Selection */}
                <div className="space-y-4">
                  {/* Universal Option */}
                  <div>
                    <div
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        preferredCms === 'universal' 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                      onClick={() => setPreferredCms('universal')}
                      data-testid="cms-option-universal"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Database className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">Universal (All Fields)</h3>
                            <p className="text-sm text-slate-500">Works with any system</p>
                          </div>
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          preferredCms === 'universal' 
                            ? 'border-teal-500 bg-teal-500' 
                            : 'border-slate-300'
                        }`}>
                          {preferredCms === 'universal' && <Check className="w-4 h-4 text-white" />}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Popular CMS Options */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      <span className="text-sm font-semibold text-slate-700">Most Popular</span>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      {POPULAR_CMS.map(cms => (
                        <div
                          key={cms.id}
                          className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            preferredCms === cms.id 
                              ? 'border-teal-500 bg-teal-50' 
                              : 'border-slate-200 hover:border-teal-300'
                          }`}
                          onClick={() => setPreferredCms(cms.id)}
                          data-testid={`cms-option-${cms.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                                <Star className="w-5 h-5 text-amber-500" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-900">{cms.name}</h3>
                                <p className="text-sm text-slate-500">Popular choice</p>
                              </div>
                            </div>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              preferredCms === cms.id 
                                ? 'border-teal-500 bg-teal-500' 
                                : 'border-slate-300'
                            }`}>
                              {preferredCms === cms.id && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* View All Options Toggle */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowAllCms(!showAllCms)}
                      className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors"
                    >
                      {showAllCms ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide additional options
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          View all 20+ CMS options
                        </>
                      )}
                    </button>
                    
                    {showAllCms && (
                      <div className="mt-3 space-y-4 pt-3 border-t border-slate-200">
                        {/* Legal CMS */}
                        <div>
                          <p className="text-sm font-medium text-slate-500 mb-2">Legal Case Management</p>
                          <div className="grid grid-cols-2 gap-2">
                            {CMS_OPTIONS.filter(cms => 
                              cms.category === 'legal' && 
                              !POPULAR_CMS.some(p => p.id === cms.id)
                            ).map(cms => (
                              <div
                                key={cms.id}
                                className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                  preferredCms === cms.id 
                                    ? 'border-teal-500 bg-teal-50' 
                                    : 'border-slate-200 hover:border-teal-300'
                                }`}
                                onClick={() => setPreferredCms(cms.id)}
                                data-testid={`cms-option-${cms.id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-slate-900">{cms.name}</span>
                                  {preferredCms === cms.id && <Check className="w-4 h-4 text-teal-500" />}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Plaintiff Systems */}
                        <div>
                          <p className="text-sm font-medium text-slate-500 mb-2">Plaintiff Case Management</p>
                          <div className="grid grid-cols-2 gap-2">
                            {CMS_OPTIONS.filter(cms => 
                              cms.category === 'plaintiff' && 
                              !POPULAR_CMS.some(p => p.id === cms.id)
                            ).map(cms => (
                              <div
                                key={cms.id}
                                className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                  preferredCms === cms.id 
                                    ? 'border-teal-500 bg-teal-50' 
                                    : 'border-slate-200 hover:border-teal-300'
                                }`}
                                onClick={() => setPreferredCms(cms.id)}
                                data-testid={`cms-option-${cms.id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-slate-900">{cms.name}</span>
                                  {preferredCms === cms.id && <Check className="w-4 h-4 text-teal-500" />}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* CRM Systems */}
                        <div>
                          <p className="text-sm font-medium text-slate-500 mb-2">CRM Systems</p>
                          <div className="grid grid-cols-2 gap-2">
                            {CMS_OPTIONS.filter(cms => cms.category === 'crm').map(cms => (
                              <div
                                key={cms.id}
                                className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                  preferredCms === cms.id 
                                    ? 'border-teal-500 bg-teal-50' 
                                    : 'border-slate-200 hover:border-teal-300'
                                }`}
                                onClick={() => setPreferredCms(cms.id)}
                                data-testid={`cms-option-${cms.id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-slate-900">{cms.name}</span>
                                  {preferredCms === cms.id && <Check className="w-4 h-4 text-teal-500" />}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Button 
                  onClick={saveCmsPreference} 
                  disabled={saving}
                  className="w-full bg-teal-500 hover:bg-teal-600"
                  data-testid="wizard-save-cms"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save CMS Preference
                </Button>
              </CardContent>
            </>
          )}

          {/* Step 3: Embed */}
          {currentStep === 3 && (
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Install Your Widget
                </CardTitle>
                <CardDescription className="text-base">
                  Add this code to your website to start capturing leads
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-4">
                {/* Embed Code */}
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-sm overflow-x-auto">
                    <code>{embedCode}</code>
                  </pre>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-3 right-3"
                    onClick={copyEmbed}
                    data-testid="wizard-copy-embed"
                  >
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>

                {/* Instructions */}
                <div className="bg-teal-50 p-4 rounded-xl border border-teal-200">
                  <h4 className="font-semibold text-teal-900 mb-2">Quick Install Instructions</h4>
                  <ol className="text-sm text-teal-800 space-y-2 list-decimal list-inside">
                    <li>Copy the code above</li>
                    <li>Paste it just before the closing <code className="bg-teal-100 px-1 rounded">&lt;/body&gt;</code> tag</li>
                    <li>The chat widget will appear on your website!</li>
                  </ol>
                </div>

                {/* Platform-specific guides */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-600">Need help with a specific platform?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Link 
                      to="/embed" 
                      className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <Code className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="font-medium text-sm text-slate-900">View All Options</p>
                        <p className="text-xs text-slate-500">WordPress, Shopify, Wix & more</p>
                      </div>
                    </Link>
                    <Link 
                      to="/chat-demo" 
                      target="_blank"
                      className="flex items-center gap-2 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <MessageSquare className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="font-medium text-sm text-slate-900">Preview Widget</p>
                        <p className="text-xs text-slate-500">See it in action</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 4: All Set / Help Center */}
          {currentStep === 4 && (
            <>
              <CardHeader className="text-center pb-4">
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  You&apos;re All Set!
                </CardTitle>
                <p className="text-slate-600 mt-2">
                  Your IntakePal is ready to capture leads. Here are some resources to help you get the most out of it.
                </p>
              </CardHeader>
              <CardContent className="space-y-6 pt-2">
                {/* Help Center Card */}
                <Link 
                  to="/help"
                  className="block p-5 bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl hover:shadow-md transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-teal-900 text-lg mb-1 group-hover:text-teal-700">
                        Explore the Help Center
                      </h3>
                      <p className="text-sm text-teal-700 mb-3">
                        Watch video tutorials, browse FAQs, and learn best practices for converting more leads.
                      </p>
                      <div className="flex items-center text-teal-600 font-medium text-sm">
                        Go to Help Center <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>

                {/* Quick Links */}
                <div className="grid grid-cols-2 gap-3">
                  <Link 
                    to="/dashboard"
                    className="flex items-center gap-3 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">Dashboard</p>
                      <p className="text-xs text-slate-500">View your leads</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/chat-demo"
                    target="_blank"
                    className="flex items-center gap-3 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Play className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">Test Widget</p>
                      <p className="text-xs text-slate-500">Try it yourself</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/branding"
                    className="flex items-center gap-3 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Palette className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">Branding</p>
                      <p className="text-xs text-slate-500">Customize more</p>
                    </div>
                  </Link>
                  
                  <Link 
                    to="/notifications"
                    className="flex items-center gap-3 p-4 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <HelpCircle className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">Notifications</p>
                      <p className="text-xs text-slate-500">Alert settings</p>
                    </div>
                  </Link>
                </div>

                {/* Pro Tips */}
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <Star className="w-4 h-4" /> Pro Tips
                  </h4>
                  <ul className="text-sm text-amber-800 space-y-1">
                    <li>• Respond to leads within 5 minutes for best conversion</li>
                    <li>• Set up SMS notifications for instant alerts</li>
                    <li>• Review the Help Center for advanced features</li>
                  </ul>
                </div>
              </CardContent>
            </>
          )}

          {/* Navigation Buttons */}
          <div className="px-6 pb-6 pt-2 flex items-center justify-between border-t border-slate-100 mt-4">
            {currentStep < 4 ? (
              <>
                <Button
                  variant="ghost"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="text-slate-600"
                  data-testid="wizard-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>

                <Button
                  variant="ghost"
                  onClick={skipStep}
                  className="text-slate-400 hover:text-slate-600"
                  data-testid="wizard-skip-btn"
                >
                  Skip
                </Button>

                <Button
                  onClick={nextStep}
                  className="bg-teal-500 hover:bg-teal-600"
                  data-testid="wizard-next-btn"
                >
                  {currentStep === 3 ? 'Complete Setup' : 'Next'}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            ) : (
              <div className="w-full flex justify-center">
                <Button
                  onClick={handleCompleteSetup}
                  className="bg-teal-500 hover:bg-teal-600 px-8"
                  data-testid="wizard-complete-btn"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  Go to Dashboard
                </Button>
              </div>
            )}
          </div>
        </Card>

        {/* Help Section */}
        <div className="mt-8 text-center">
          <p className="text-sm text-slate-500">
            Need help? Check our{' '}
            <Link to="/help" className="text-teal-600 hover:underline">Help Center</Link>
            {' '}or contact support.
          </p>
        </div>
      </main>
    </div>
  );
};

export default SetupWizardPage;
